import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CardContent from '@mui/material/CardContent';
import { LinearProgress, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useRequest } from '../../hooks';
import FileModel from './model/FileModel';

const DragZoneSpace = ({ openExplorer = () => {}, extControl = false, disabled = false }) => {
  return (
    <div className="flex flex-col h-full flex-auto justify-center items-center flex-wrap">
      <UploadFileIcon
        sx={{ width: '8rem', height: '8rem', color: '#9ca3af', paddingBottom: '16px' }}
      />
      <Typography align="center">Arrastra sobre el recuadro un archivo o bien:</Typography>
      <div
        className="mt-32 flex flex-row justify-center items-center flex-wrap"
        style={{ zIndex: 10000 }}
      >
        <div className="mx-8">
          <Button variant="outlined" size="small" onClick={openExplorer} disabled={disabled}>
            Subir desde computadora
          </Button>
        </div>
      </div>
    </div>
  );
};

const funcPrototype = () => new Promise();

const getDefaults = () => ({
  title: 'title',
});

const defaultErrEnums = {
  'too-many-files': 'Solo se permite cargar un archivo',
  'file-too-large': 'El archivo no debe rebasar los 25000kB',
  'unsupported-format': 'El formato de archivo no es compatible',
  'upload-failed': 'La carga del archivo ha fallado, por favor inténtelo de nuevo',
  'file-invalid-type': 'Formato de archivo no valido',
};

const defaultChunkSize = 100; // 100MB;

/**
 * Componente para seleccionar y subir archivos con funcionalidades de arrastrar y soltar.
 * Permite configurar parámetros como el tamaño máximo del archivo, los formatos aceptados,
 * y ejecutar acciones antes y después de subir un archivo.
 *
 * @component
 * @param {Object} props - Las propiedades del componente.
 * @param {Function} props.setFile - Función para establecer el archivo seleccionado.
 * @param {Object} props.fileEqualsTo - Parámetros para comparar el archivo subido con el archivo en el servidor.
 * @param {Function} props.storeRequest - Función para realizar la petición de almacenamiento del archivo.
 * @param {Function} props.deleteRequest - Función para realizar la petición de eliminación del archivo.
 * @param {Object} props.formDataParams - Parámetros adicionales a enviar en la petición de subida del archivo.
 * @param {number} props.maxSize - Tamaño máximo permitido para los archivos en kilobytes.
 * @param {boolean} [props.disabled=false] - Deshabilita la interacción con el componente.
 * @param {boolean} [props.replaceOnly=false] - Habilita solo la opción de reemplazar el archivo, deshabilitando la eliminación.
 * @param {boolean} [props.uploadAsChunk=false] - Habilita subida de archivos por partes y peticion serializada.
 * @param {string[]} props.formats - Lista de formatos de archivo aceptados.
 * @param {Function} [props.onDeleteUpload=()=>{}] - Callback a ejecutar después de eliminar un archivo.
 * @param {Function} [props.onBeforeUpload=()=>{}] - Callback a ejecutar antes de subir un archivo.
 * @param {Function} [props.onAfterUpload=()=>{}] - Callback a ejecutar después de subir un archivo.
 * @param {Function} [props.onMessage=()=>{}] - Callback para manejar mensajes o errores durante las peticiones.
 * @param {number} props.chunkSize - Define el tamaño de cada parte serializada del archivo, debe estar habilitada la opcion upoadAsChunk
 * @returns {JSX.Element} El componente FilePicker.
 *
 * @example
 * <FilePicker
 *   setFile={setFileHandler}
 *   storeRequest={storeFileHandler}
 *   deleteRequest={deleteFileHandler}
 *   formats={{ 'application/pdf': ['.pdf'] }}
 *   maxSize={5000}
 * />
 */
export const FilePicker = ({
  setFile,
  fileEqualsTo,
  storeRequest,
  deleteRequest,
  formDataParams,
  maxSize,
  uploadAsChunk = false,
  disabled = false,
  replaceOnly = false,
  formats,
  onDeleteUpload = () => {},
  onBeforeUpload = () => {},
  onAfterUpload = () => {},
  onMessage = () => {},
  chunkSize = defaultChunkSize,
}) => {
  const storeWrapper = typeof storeRequest === 'function' ? storeRequest : funcPrototype;
  const deleteWrapper = typeof storeRequest === 'function' ? deleteRequest : funcPrototype;
  const [files, setFiles] = useState([]);
  const [canvasFile, setCanvasFile] = useState(undefined);
  const [fileObject, setFileObject] = useState(undefined);
  const [fileName, setFileName] = useState(undefined);
  const [errEnums, setErrEnums] = useState(defaultErrEnums);
  const [currentErrEnum, setCurrentErrEnum] = useState(undefined);
  const [wasSavedToServer, setWasSavedToServer] = useState(false);
  const [progressAllSrcDownload, setProgressAllSrcDownload] = useState(0);
  const [isLoadingChunks, setIsLoadingChunks] = useState(false);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (maxSize === undefined) return;
    setErrEnums((current) => {
      return {
        ...current,
        'file-too-large': `El archivo no debe rebasar los ${maxSize}kB`,
      };
    });
  }, [maxSize]);

  const {
    makeRequest: storeFile,
    isLoading: isLoadingStore,
    response: responseStore,
    status: statusStore,
  } = useRequest(storeWrapper);

  const { makeRequest: deleteFile } = useRequest(deleteWrapper);

  useEffect(() => {
    if (!setFile) {
      return;
    }
    setFileObject(setFile);
    setWasSavedToServer(true);
  }, [setFile]);

  const { getRootProps, getInputProps, open, isDragAccept, fileRejections, acceptedFiles } =
    useDropzone({
      noClick: true,
      noKeyboard: true,
      maxFiles: 1,
      accept: formats,
      maxSize: maxSize ? maxSize * 1024 : undefined,
      disabled,
    });

  const handleOnChange = (fileList) => {
    setCurrentErrEnum(undefined);
    if (disabled) return;
    setFiles(fileList);
    setCanvasFile(fileList[0]);
  };

  const handleClearAll = () => {
    setCanvasFile(undefined);
    setFiles([]);
    setFileObject(undefined);
    setWasSavedToServer(false);
    setFileName(undefined);
  };

  const handleRejectedFiles = (rejectedListFiles) => {
    if (rejectedListFiles.length <= 0) return;
    const rejectedFileErrors = rejectedListFiles[0].errors;
    if (typeof rejectedFileErrors !== 'object' || rejectedFileErrors.length <= 0) return;
    setCurrentErrEnum(rejectedFileErrors[0].code.toString());
    const accumulatedString = rejectedFileErrors
      .map(({ code, message }) => {
        onMessage(`${code}: ${errEnums[code]}`);
        return `Code: ${code}, Message: ${message}`;
      })
      .join('\n');
    console.warn(accumulatedString);
  };

  const onUploadProgress = ({ loaded, total }) => {
    const fetchingPercent = Math.floor((loaded * 100) / total);
    setProgressAllSrcDownload(fetchingPercent);
  };

  const handleResponseRequest = (reqRes) => {
    console.debug(reqRes);
    if (reqRes === undefined || reqRes === null) {
      setWasSavedToServer(false);
      return;
    }
    setWasSavedToServer(true);
    setFileObject(reqRes);
  };

  const uploadAsSingle = (file) => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    const formData = new FormData();
    const alterProps = {
      ...formDataParams,
      title: formDataParams?.title || formDataParams?.name || file.name,
    };
    const extraMetadata = FileModel(alterProps);
    Object.keys(extraMetadata).forEach((paramKey) => {
      formData.append(`${paramKey}`, extraMetadata[paramKey]);
    });
    formData.append('file', file);
    if (typeof storeRequest === 'function') {
      storeFile(formData, { onUploadProgress, signal })
        .then(({ isOk, errorContent }) => {
          const message = isOk
            ? 'Archivo cargado correctamente'
            : errorContent?.message || 'Ha ocurrido un error';
          console.debug(message);
          onMessage(message);
        })
        .finally(() => {
          setProgressAllSrcDownload(0);
        });
    }
  };

  const recursiveLoad = async (file, extraMetadata) => {
    if (file === undefined) return;
    abortControllerRef.current = new AbortController();

    setIsLoadingChunks(true);
    const chunkBytesSize = chunkSize * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkBytesSize);

    console.info(
      `uuid: ${extraMetadata.uuid} \nname: ${file.name} \nsize: ${
        file.size / 1024 / 1024
      }Mbytes \ntotalChunks: ${totalChunks}`
    );

    async function loadNext(currentChunk = 0) {
      if (currentChunk + 1 > totalChunks) {
        setProgressAllSrcDownload(0);
        setIsLoadingChunks(false);
        return;
      }
      const index = currentChunk;
      const start = index * chunkBytesSize;
      const end = Math.min(file.size, start + chunkBytesSize);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      if (!_.isEmpty(extraMetadata)) {
        Object.keys(extraMetadata).forEach((paramKey) => {
          formData.append(`${paramKey}`, extraMetadata[paramKey]);
        });
      }
      formData.append('file', chunk);
      formData.append('fileName', file.name);
      formData.append('chunkIndex', index.toString());
      formData.append('totalChunks', totalChunks.toString());
      const chunkSizeTemp = end - start;
      console.info(
        `chunk: ${index + 1} de ${totalChunks} | chunkSize: ${chunkSizeTemp / 1024 / 1024}Mbytes`
      );
      try {
        const { isOk, successContent, errorContent } = await storeFile(formData, {
          onUploadProgress: ({ loaded }) =>
            onUploadProgress({ loaded: loaded + start, total: file.size }),
          signal: abortControllerRef.current.signal,
        });
        if (!isOk) {
          setProgressAllSrcDownload(0);
          setIsLoadingChunks(false);
          onMessage(errorContent?.message || 'Ha ocurrido un error');
          return;
        }
        if (currentChunk + 1 >= totalChunks) {
          handleResponseRequest(successContent);
          onMessage('Archivo cargado correctamente');
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          console.debug('Subida abortada por el usuario');
        } else {
          console.error(err);
          onMessage('Ocurrió un error durante la carga');
        }
        setProgressAllSrcDownload(0);
        setIsLoadingChunks(false);
        return;
      }

      const tryTimeOutFetch = setTimeout(async () => {
        await loadNext(currentChunk + 1);
        clearTimeout(tryTimeOutFetch);
      }, 1000);
    }

    await loadNext(0);
  };

  const uploadAsChunks = async (file) => {
    const uuid = uuidv4();
    const alterProps = {
      ...formDataParams,
      uuid,
      title: formDataParams?.title || formDataParams?.name || file.name,
    };
    const extraMetadata = FileModel(alterProps);
    await recursiveLoad(file, extraMetadata);
  };

  const handleUpload = (file) => {
    if (typeof onBeforeUpload === 'function') {
      onBeforeUpload(file);
    }
    if (!uploadAsChunk) {
      uploadAsSingle(file);
      return;
    }
    uploadAsChunks(file);
  };

  const handleDeleteFile = () => {
    if (typeof deleteRequest === 'function') {
      deleteFile(fileObject)
        .then(({ isOk }) => {
          const message = isOk
            ? 'Archivo borrado correctamente'
            : 'Ha ocurrido un error al eliminar el archivo';
          console.debug(message);
          onMessage(message);
          if (isOk) {
            handleClearAll();
            onDeleteUpload(undefined);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      return;
    }
    handleClearAll();
    onDeleteUpload();
  };

  useEffect(() => {
    if (fileObject === undefined) return;
    onAfterUpload(fileObject);
    const reference = _.merge({}, getDefaults(), fileEqualsTo);
    setFileName(fileObject[reference.title]);
  }, [fileObject]);

  useEffect(() => {
    if (acceptedFiles === undefined || acceptedFiles.length < 1) return;
    handleOnChange(acceptedFiles);
  }, [acceptedFiles]);

  useEffect(() => {
    if (fileRejections === undefined) return;
    handleRejectedFiles(fileRejections);
  }, [fileRejections]);

  useEffect(() => {
    if (uploadAsChunk) return;
    if (statusStore !== 'SUCCESS') return;
    handleResponseRequest(responseStore);
  }, [responseStore, statusStore, uploadAsChunk]);

  useEffect(() => {
    return () => {
      handleClearAll();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (canvasFile === undefined) return;
    handleUpload(canvasFile);
  }, [canvasFile]);

  return (
    <div className="w-full relative">
      <Card variant="outlined" className="p-0 border-dashed rounded-lg border-2">
        <CardContent className="p-0 m-0 overflow-hidden flex justify-center items-center">
          <Box
            className="flex flex-col w-full justify-center items-center px-24 pt-24 m-0"
            sx={{ minHeight: '24rem', cursor: 'grab' }}
          >
            {isLoadingStore || isLoadingChunks ? (
              <div className="w-full flex flex-col justify-center items-center max-w-[400px]">
                <Typography className="font-semibold text-24 my-8">{`${progressAllSrcDownload}%`}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={progressAllSrcDownload}
                  className="w-full h-4"
                  color="secondary"
                />
              </div>
            ) : wasSavedToServer ? (
              <div className="flex flex-col h-full flex-auto justify-center items-center flex-wrap">
                <InsertDriveFileIcon
                  sx={{ width: '8rem', height: '8rem', color: '#555', paddingBottom: '16px' }}
                />
                <div className="mt-4 flex flex-wrap flex-row">
                  <Typography variant="caption" display="block" gutterBottom>
                    {fileName !== undefined ? fileName : canvasFile?.name}
                  </Typography>
                </div>
                <div className="z-50 flex-wrap flex flex-row justify-center items-center">
                  {!replaceOnly && (
                    <div className="mt-8 mx-8">
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => handleDeleteFile()}
                        startIcon={<DeleteIcon />}
                      >
                        Eliminar archivo
                      </Button>
                    </div>
                  )}
                  <div className="mt-8 mx-8">
                    <Button variant="outlined" size="small" onClick={open}>
                      Subir otro archivo
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <DragZoneSpace disabled={disabled} openExplorer={open} />
            )}
          </Box>
          {!isLoadingStore && (
            <div
              {...getRootProps({ className: 'dropzone' })}
              className="absolute top-0 left-0 rounded-lg"
              style={{
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                backgroundColor: isDragAccept ? 'rgba(62, 30, 164, 0.25)' : undefined,
              }}
            >
              <input {...getInputProps()} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

FilePicker.propTypes = {
  hasGallery: PropTypes.bool,
  onAfterUpload: PropTypes.func,
  whereUpload: PropTypes.func,
  adminMode: PropTypes.bool,
};
